import { format } from "date-fns"
import { GanttContext, getIndexAsDate, getScaleRangeIncludesDate, getWeekDateRange } from "./Gantt"
import { useContext } from "react"
import { isWorkingDay } from "@/utils/GlobalConstants";

const GanttHeader = () => {
    const ganttProps = useContext(GanttContext);

    function handleYearClick(date: number) {
        const yearStart = new Date(date);
        yearStart.setMonth(0, 1); // Set to January 1st of the year
        ganttProps.setDisplayStartDate(yearStart);
        ganttProps.setDisplayLength(12);
        ganttProps.setGanttScale("month");
    }

    function handleWeekClick(date: number) {
        const weekStart = new Date(date);
        weekStart.setDate(weekStart.getDate() - weekStart.getDay() + 1); // Set to the Monday of the week
        ganttProps.setDisplayStartDate(weekStart);
        ganttProps.setDisplayLength(7);
        ganttProps.setGanttScale("day");
    }

    function handleMonthClick(date: number) {
        const month = new Date(date).getMonth();
        const year = new Date(date).getFullYear();
        const monthStart = new Date(year, month, 1);
        // const monthStartIndex = Math.floor((monthStart.getTime() - ganttProps.displayStartDate.getTime()) / (24 * 60 * 60 * 1000));
        // const newStartDate = getIndexAsDate(monthStartIndex, ganttProps.displayStartDate);
        ganttProps.setDisplayStartDate(monthStart);
        ganttProps.setDisplayLength(5);
        ganttProps.setGanttScale("week");
    }

    function handleQuarterClick(date: number, quarter: number) {
        const year = new Date(date).getFullYear();
        const quarterStartMonth = (quarter - 1) * 3; // Q1 starts in January, Q2 in April, etc.
        const quarterStart = new Date(year, quarterStartMonth, 1);
        ganttProps.setDisplayStartDate(quarterStart);
        ganttProps.setDisplayLength(3);
        ganttProps.setGanttScale("month");
    }

    return (
        <div className="flex items-center h-10 w-full">
            <div className="w-full h-full bg-table-header relative">
                <div className="absolute top-0 left-0 h-full w-full flex">
                    {Array.from({ length: ganttProps.displayLength }).map((_, index) => (
                        <div
                            key={index}
                            className={
                                "h-full text-table-header-foreground border-r border-ganttchart-gridline border-b-2 " +
                                (getScaleRangeIncludesDate(ganttProps.displayStartDate, index, ganttProps.ganttScale, new Date())
                                    ? "border-b-ganttchart-current-date-line"
                                    : "border-b-ganttchart-gridline")
                                + " " + (ganttProps.ganttScale === "day" ? isWorkingDay(getIndexAsDate(index, ganttProps.displayStartDate)) ? "bg-ganttchart-header-background" : "bg-ganttchart-header-background brightness-50" : "")
                            }
                            style={{
                                width: `${100 / ganttProps.displayLength}%`,
                            }}
                        >
                            {ganttProps.ganttScale === "day" ?
                                <>
                                    <div className="text-center">{new Date(ganttProps.displayStartDate.getTime() + index * (24 * 60 * 60 * 1000)).toLocaleDateString('en-US', { weekday: 'short' })[0]}</div>
                                    <div className="text-center text-xs">{format(new Date(ganttProps.displayStartDate.getTime() + index * (24 * 60 * 60 * 1000)), 'dd MMM')}</div>
                                </>
                                : ganttProps.ganttScale === "week" ?
                                    <div onClick={() => { handleWeekClick(ganttProps.displayStartDate.getTime() + index * (7 * 24 * 60 * 60 * 1000)) }} >
                                        <div className="text-center">
                                            {
                                                (() => {
                                                    // Calculate the Monday before or on projectStartDate
                                                    const projectStart = new Date(ganttProps.projectStartDate);
                                                    const projectStartDay = projectStart.getDay();
                                                    const projectStartMonday = new Date(projectStart);
                                                    projectStartMonday.setDate(projectStart.getDate() - ((projectStartDay + 6) % 7));

                                                    // Calculate the Monday for the current week cell
                                                    const weekStart = new Date(ganttProps.displayStartDate.getTime() + index * (7 * 24 * 60 * 60 * 1000));
                                                    const weekStartDay = weekStart.getDay();
                                                    const weekMonday = new Date(weekStart);
                                                    weekMonday.setDate(weekStart.getDate() - ((weekStartDay + 6) % 7));

                                                    // Calculate week number (1-based)
                                                    const diff = weekMonday.getTime() - projectStartMonday.getTime();
                                                    const weekNum = Math.floor(diff / (7 * 24 * 60 * 60 * 1000)) + 1;
                                                    return "Week " + weekNum;
                                                })()
                                            }
                                        </div>
                                        <div className="text-center text-xs">
                                            {"W/C " + format(getWeekDateRange(index, ganttProps.displayStartDate).startDate, 'dd-MMM')}
                                        </div>
                                    </div>
                                    : ganttProps.ganttScale === "month" ?
                                        <div onClick={() => { 
                                            const date = new Date(ganttProps.displayStartDate);
                                            date.setDate(1);
                                            date.setMonth(date.getMonth() + index);
                                            handleMonthClick(date.getTime()) 
                                        }} >
                                            <div className="text-center">
                                                {(() => {
                                                    const date = new Date(ganttProps.displayStartDate);
                                                    date.setDate(1);
                                                    date.setMonth(date.getMonth() + index);
                                                    return format(date, 'MMM');
                                                })()}
                                            </div>
                                            <div className="text-center text-xs">
                                                {(() => {
                                                    const date = new Date(ganttProps.displayStartDate);
                                                    date.setDate(1);
                                                    date.setMonth(date.getMonth() + index);
                                                    return format(date, 'yyyy');
                                                })()}
                                            </div>
                                        </div>
                                        : ganttProps.ganttScale === "year" ?
                                            <div>
                                                <div className="text-center" onClick={() => handleYearClick(ganttProps.displayStartDate.getTime() + index * (365 * 24 * 60 * 60 * 1000))} >
                                                    {format(new Date(ganttProps.displayStartDate.getTime() + index * (365 * 24 * 60 * 60 * 1000)), 'yyyy')}
                                                </div>
                                                <div className="flex flex-row justify-center">
                                                    <div className="w-1/4 text-center text-xs border-t border-ganttchart-gridline" onClick={() => handleQuarterClick(ganttProps.displayStartDate.getTime() + index * (365 * 24 * 60 * 60 * 1000),1)} >Q1</div>
                                                    <div className="w-1/4 text-center text-xs border-t border-l border-ganttchart-gridline" onClick={() => handleQuarterClick(ganttProps.displayStartDate.getTime() + index * (365 * 24 * 60 * 60 * 1000),2)} >Q2</div>
                                                    <div className="w-1/4 text-center text-xs border-t border-l border-ganttchart-gridline" onClick={() => handleQuarterClick(ganttProps.displayStartDate.getTime() + index * (365 * 24 * 60 * 60 * 1000),3)} >Q3</div>
                                                    <div className="w-1/4 text-center text-xs border-t border-l border-ganttchart-gridline" onClick={() => handleQuarterClick(ganttProps.displayStartDate.getTime() + index * (365 * 24 * 60 * 60 * 1000),4)} >Q4</div>
                                                </div>
                                            </div>
                                            : null}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    )
}

export default GanttHeader